import cn from 'classnames'
import type {
  ChangeEvent,
  DetailedHTMLProps,
  MutableRefObject,
  TextareaHTMLAttributes,
} from 'react'
import React, { useEffect, useRef, useState } from 'react'

import { isMixed } from 'components/uiKit/KitUtils'
import { testProps } from 'utils/test/qaData'

import type { IKitControlProps } from '../KitTypes'
import { KitSize } from '../KitTypes'
import * as s from './TextArea.module.scss'
import { VERTICAL_PADDING_MEDIUM, VERTICAL_PADDING_MINI } from './constants'
import { useAutoHeight } from './useAutoHeight'

const DEFAULT_SIZE = { minRows: 4, maxRows: 4 }
type TextAreaDefaultTypes = Omit<
  DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
  'onChange' | 'defaultValue' | 'name' | 'onBlur' | 'onFocus' | 'value'
>

export type TextAreaRef = HTMLTextAreaElement

export type AutoHeightProp = boolean | { minRows?: number; maxRows?: number }

export interface ITextArea extends TextAreaDefaultTypes, IKitControlProps<string> {
  placeholder?: string
  autoSize?: AutoHeightProp
  styleType?: 'base' | 'ghost' | 'clear'
  fluid?: boolean
  ref?:
    | ((instance: HTMLTextAreaElement | null) => void)
    | React.MutableRefObject<HTMLTextAreaElement | null>
    | null
  postfix?: React.ReactNode
  maxWidth?: number
  lineHeight?: number
}

const TextArea = React.forwardRef<HTMLTextAreaElement, ITextArea>(
  (
    {
      value: valueProp = '',
      defaultValue: _defaultValue,
      postfix,
      onChange,
      autoFocus,
      autoSize = DEFAULT_SIZE,
      styleType = 'base',
      size = KitSize.M,
      fluid = true,
      error,
      maxWidth,
      onFocus,
      onBlur,
      lineHeight,
      ...rest
    },
    ref,
  ) => {
    const value = isMixed(valueProp) ? '' : (valueProp as string)
    const [isFocus, setIsFocus] = useState(false)
    const onFocusHandle = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      onFocus?.(event)
      setIsFocus(true)
    }

    const onBlurHandle = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      onBlur?.(event)
      setIsFocus(false)
    }

    const localRef = useRef<TextAreaRef>(null)
    const textareaElement = (ref as MutableRefObject<HTMLTextAreaElement | null>) || localRef
    const [pos, setPos] = useState(0)

    const verticalPadding = size === 'mini' ? VERTICAL_PADDING_MINI : VERTICAL_PADDING_MEDIUM
    const cssVars = { '--verticalPadding': `${verticalPadding}px`, maxWidth } as React.CSSProperties

    const handleChangeTextArea = (e: ChangeEvent<HTMLTextAreaElement>) => {
      setPos(e.target.selectionStart)
      onChange?.(e.target.value /* , e */)
    }

    useEffect(() => {
      if (textareaElement.current) {
        textareaElement.current.selectionStart = pos
        textareaElement.current.selectionEnd = pos
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, pos])

    useAutoHeight(textareaElement, verticalPadding, autoSize, [value, verticalPadding], lineHeight)

    useEffect(() => {
      if (autoFocus) {
        // hotfix scrolling when focusing element outside viewport
        setTimeout(() => {
          if (textareaElement.current) {
            textareaElement.current.focus({ preventScroll: true })
            textareaElement.current.selectionStart = textareaElement.current.value.length
            textareaElement.current.selectionEnd = textareaElement.current.value.length
          }
        }, 0)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <div
        className={cn(s.root, { [s.fluid]: fluid, [s.additionRow]: lineHeight && !!value })}
        style={cssVars}
      >
        <textarea
          {...rest}
          {...testProps({
            el: 'textarea',
            value,
            name: rest.name,
            disabled: rest.disabled,
            focus: isFocus,
          })}
          className={cn(s.textarea, s[styleType], s[size], {
            [s.disabled]: rest.disabled,
            [s.error]: error,
            [s.hasPostfix]: postfix,
          })}
          onBlur={onBlurHandle}
          onChange={handleChangeTextArea}
          onFocus={onFocusHandle}
          ref={textareaElement}
          rows={lineHeight ? 1 : undefined}
          value={value}
        />
        {postfix && (
          <div className={cn(s.postfix, { [s.additionRow]: lineHeight && !!value })}>{postfix}</div>
        )}
      </div>
    )
  },
)

TextArea.displayName = 'TextArea'

export default React.memo(TextArea)
